import {Opportunity} from './pages/Opportunity';
import {ProposalBuilder} from './pages/ProposalBuilder';
import {CarpetRun} from './pages/CarpetRun';
import {PurchaseOrders,PurchaseOrderDetail} from './pages/PurchaseOrders';
import Mail from './pages/Mail';
import {Installers,InstallerDetailPage} from './pages/Installers';
import {Inventory} from './pages/Inventory';
import {HistoricalQuotePage} from './pages/HistoricalQuote';
import {LegacyOpportunity} from './pages/LegacyOpportunity';
import {EmmaDesk} from "./pages/EmmaDesk";
import {EmmaChat} from "./pages/EmmaChat";
import { useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getTestSet } from "./lib/testset";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Frame } from "./shell/Frame";
import { AppError } from "./shell/AppError";
import { BuildTracker } from "./pages/BuildTracker";
import { Today } from "./pages/Today";
import { Archive } from "./pages/Archive";
import { Leads } from "./pages/Leads";
import { HipageOffers } from "./pages/HipageOffers";
import { Pipeline } from "./pages/Pipeline";
import { Catalogue } from "./pages/Catalogue";
import { PriceMyFloor } from "./pages/PriceMyFloor";
import { Jobs } from "./pages/Jobs";
import { Job } from "./pages/Job";
import { Contacts } from "./pages/Contacts";
import { Accounts } from "./pages/Accounts";
import { Account } from "./pages/Account";
import { Contact } from "./pages/Contact";
import {Attention} from "./pages/Attention";
import { FollowUps } from "./pages/FollowUps";
import { Calendar } from "./pages/Calendar";
import { Marketing } from "./pages/Marketing";
import { Showcase } from "./pages/Showcase";
import { Website } from "./pages/Website";
import { WebsiteSection } from "./components/website-nav";
import { Members } from "./pages/Members";
import { Suppliers, SupplierDetail } from "./pages/Suppliers";
import { Settings } from "./pages/Settings";
import { Placeholder } from "./pages/Placeholder";
import { SignIn } from "./pages/SignIn";
import { currentSession, supabase } from "./lib/auth";
import { env } from "./lib/env";
import { LanguageProvider, useCrmLang } from "./lib/i18n";
import { WorkRecordReview } from "./pages/WorkRecordReview";
import {Communications} from './pages/Communications';

export function App() {
  const [identity, setIdentity] = useState<string | null | undefined>(env.mock ? "mock" : undefined);
  const scope = useSyncExternalStore(subscribeScope, getTestSet, () => null);
  const authed = identity === undefined ? null : !!identity;
  useEffect(() => {
    if (env.mock) return;
    let active = true, observed = false;
    const sub = supabase()?.auth.onAuthStateChange((_e, session) => { observed = true; if (active) setIdentity(session?.user.id ?? null); });
    void currentSession().then(session => { if (active && !observed) setIdentity(session?.user.id ?? null); });
    return () => { active = false; sub?.data.subscription.unsubscribe(); };
  }, []);
  if (authed === null) return <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">Signing you in…</div>;
  if (!authed) return <SignIn />;
  return (
    <LanguageRoot><SessionWorkspace key={`${identity}:${scope ?? "live"}`}>
      {/* A CRASH MUST NEVER BE A BLANK SCREEN. Jacky: *"no error, click on drawer gets me blank
          screen"* — React unmounts the whole tree when a render throws and nothing catches it, so
          one bad value in one component takes the entire CRM away and leaves white, with nothing to
          report. The boundary sits here: outside every page and the EmmA drawer, inside the language
          provider and the query cache, so the failure is contained to the panel that failed and the
          message names the error. */}
      <AppError where="This screen">
      <BrowserRouter>
      <Routes>
        <Route element={<Frame />}>
          <Route index element={<Today />} />
          <Route path="emma" element={<EmmaChat />} />
          <Route path="emma/work" element={<EmmaDesk />} />
          <Route path="proposals/new" element={<ProposalBuilder />} />
          <Route path="proposals/:id" element={<ProposalBuilder />} />
          <Route path="leads" element={<Leads />} />
          <Route path="hipages" element={<HipageOffers />} />
          <Route path="opportunities/:ref" element={<Opportunity />} />
          <Route path="pipeline" element={<Pipeline />} />
          <Route path="catalogue" element={<Catalogue />} />
          <Route path="service-library" element={<Settings libraryOnly />} />
          <Route element={<WebsiteSection />}>
            <Route path="website" element={<Website />} />
            <Route path="price-my-floor" element={<PriceMyFloor />} />
            <Route path="marketing" element={<Marketing />} />
            <Route path="showcase" element={<Showcase />} />
          </Route>
          <Route path="pipeline/legacy/:ref" element={<LegacyOpportunity />} />
          <Route path="attention" element={<Attention />} />
          <Route path="follow-ups" element={<FollowUps />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="contacts/:id" element={<Contact />} />
          <Route path="accounts" element={<Accounts />} />
          <Route path="accounts/:id" element={<Account />} />
          <Route path="jobs" element={<Jobs />} />
          <Route path="jobs/:ref" element={<Job />} />
          <Route path="archive" element={<Archive />} />
          <Route path="calendar" element={<Calendar cacheScope={identity!} />} />
          <Route path="members" element={<Members />} />
          <Route path="suppliers" element={<Suppliers />} />
          <Route path="suppliers/:id" element={<SupplierDetail />} />
          <Route path="mail" element={<Mail />} />
          <Route path="communications" element={<Communications />} />
          <Route path="purchase-orders" element={<PurchaseOrders />} />
          <Route path="purchase-orders/:po" element={<PurchaseOrderDetail />} />
          <Route path="installers" element={<Installers />} />
          <Route path="installers/:party" element={<InstallerDetailPage />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="quotes/:id" element={<HistoricalQuotePage />} />
          <Route path="build-tracker" element={<BuildTracker />} />
          <Route path="work-record-review" element={<WorkRecordReview />} />
          <Route path="work-record/:ref" element={<WorkRecordReview />} />
          <Route path="carpet-run/:ref" element={<CarpetRun />} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Placeholder title="Not here" note="No such page." />} />
        </Route>
      </Routes>
    </BrowserRouter>
      </AppError></SessionWorkspace></LanguageRoot>
  );
}

/** The language lives above everything: the rail, the top bar and every page read
 *  from it, and the choice outlives navigation (board card AIW-CN). */
function LanguageRoot({ children }: { children: ReactNode }) {
  const lang = useCrmLang();
  return <LanguageProvider state={lang}>{children}</LanguageProvider>;
}

function subscribeScope(changed: () => void) {
  window.addEventListener("aiw:testset", changed); window.addEventListener("storage", changed);
  return () => { window.removeEventListener("aiw:testset", changed); window.removeEventListener("storage", changed); };
}
/** A fresh cache and mounted workspace for every identity/test session. Old
 * requests may finish, but can never populate the new session's cache. */
function SessionWorkspace({ children }: { children: ReactNode }) {
  const [client] = useState(() => new QueryClient({ defaultOptions: { queries: { staleTime: 15_000, retry: 1, refetchOnWindowFocus: false } } }));
  useEffect(() => () => { void client.cancelQueries(); client.clear(); }, [client]);
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
